import axios, { AxiosError } from 'axios';
import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';

interface ServerStatusContextValue {
  isBackendUp: boolean;
  message: string | null;
  failureId: number;
  lastChecked: Date | null;
}

const ServerStatusContext = createContext<ServerStatusContextValue | undefined>(undefined);

export const ServerStatusProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isBackendUp, setIsBackendUp] = useState(true);
  const [message, setMessage] = useState<string | null>(null);
  const [failureId, setFailureId] = useState(0);
  const [lastChecked, setLastChecked] = useState<Date | null>(null);

  useEffect(() => {
    const responseInterceptor = axios.interceptors.response.use(
      (response) => {
        setIsBackendUp(true);
        setMessage(null);
        return response;
      },
      (error: AxiosError) => {
        const status = error.response?.status;
        const networkError = !error.response;
        if (networkError || (status && status >= 500)) {
          const reason = networkError
            ? 'The realm is unreachable right now. Please retry in a moment.'
            : `The server returned ${status}. Our keepers are tending the machines.`;
          setIsBackendUp(false);
          setMessage(reason);
          setFailureId((id) => id + 1);
          setLastChecked(new Date());
        }
        return Promise.reject(error);
      }
    );

    return () => {
      axios.interceptors.response.eject(responseInterceptor);
    };
  }, []);

  const value = useMemo(
    () => ({ isBackendUp, message, failureId, lastChecked }),
    [isBackendUp, message, failureId, lastChecked]
  );

  return <ServerStatusContext.Provider value={value}>{children}</ServerStatusContext.Provider>;
};

export const useServerStatus = (): ServerStatusContextValue => {
  const context = useContext(ServerStatusContext);
  if (!context) {
    throw new Error('useServerStatus must be used within a ServerStatusProvider');
  }
  return context;
};
